import React, { useState, useEffect } from 'react';
import 'react-pro-sidebar/dist/css/styles.css';
import {
  ProSidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  Menu,
  MenuItem
} from 'react-pro-sidebar';
import {
  Box,
  IconButton,
  Typography,
  useTheme
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { tokens } from '../../theme';

import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ClassOutlinedIcon from '@mui/icons-material/ClassOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import alamoLogo from '../../assets/alamo-logo.webp';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { isAdmin } from '../../lib/auth';

const SIDEBAR_WIDTH = 220;
const SIDEBAR_COLLAPSED = 80;

const Sidebar = ({ onSidebarWidth }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [collapsed, setCollapsed] = useState(true);
  const [hovered, setHovered] = useState(false);
  const location = useLocation();
  const [selected, setSelected] = useState('Intake');

  useEffect(() => {
    if (onSidebarWidth) {
      onSidebarWidth(collapsed && !hovered ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH);
    }
  }, [collapsed, hovered, onSidebarWidth]);

  const isCollapsed = collapsed && !hovered;

  const toggleCollapsed = () => setCollapsed(!collapsed);

  useEffect(() => {
    const path = location.pathname;
    if (path === '/' || path.startsWith('/calendar-intake')) setSelected('Intake');
    else if (path.startsWith('/recents')) setSelected('Recents');
    else if (path.startsWith('/dashboard')) setSelected('Dashboard');
    else if (path.startsWith('/students')) setSelected('Students');
    else if (path.startsWith('/classes')) setSelected('Classes');
    else if (path.startsWith('/instructors')) setSelected('Instructors');
    else if (path.startsWith('/reports')) setSelected('Reports');
    else if (path.startsWith('/rosters')) setSelected('Rosters');
    else if (path.startsWith('/payments')) setSelected('Payments');
    else if (path.startsWith('/admin/audit-logs')) setSelected('Audit Logs');
    else if (path.startsWith('/admin/ai-keys')) setSelected('AI Keys');
  }, [location.pathname]);

  const Item = ({ title, to, icon }) => (
    <MenuItem
      active={selected === title}
      icon={icon}
      style={{ color: colors.grey[100] }}
      onClick={() => setSelected(title)}
    >
      <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
        <Typography>{title}</Typography>
      </Link>
    </MenuItem>
  );

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        alignSelf: 'flex-start',
        height: '100vh',
        zIndex: 100,
        transition: 'width 0.2s',
        width: isCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH,
        '& .pro-sidebar-inner': {
          background: `${colors.alamoRed[700]} !important`
        },
        '& .pro-menu-item.active': {
          backgroundColor: `${colors.alamoOrange[600]} !important`
        },
        '& .pro-menu-item:hover': {
          backgroundColor: `${colors.alamoRed[500]} !important`
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <ProSidebar collapsed={isCollapsed} width={isCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH}>
        <SidebarHeader>
          <Box
            display='flex'
            alignItems='center'
            justifyContent={isCollapsed ? 'center' : 'space-between'}
            flexDirection={isCollapsed ? 'column' : 'row'}
            gap={isCollapsed ? 0.5 : 1}
            p={isCollapsed ? 1 : 2}
          >
            {!isCollapsed && (
              <Box
                component='img'
                src={alamoLogo}
                alt='Alamo Center'
                sx={{
                  width: 138,
                  maxHeight: 42,
                  objectFit: 'contain',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '8px',
                  px: 1,
                  py: 0.75
                }}
              />
            )}
            {isCollapsed && (
              <Box
                component='img'
                src={alamoLogo}
                alt='Alamo Center'
                sx={{
                  width: 42,
                  height: 42,
                  objectFit: 'contain',
                  objectPosition: 'left center',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '8px',
                  p: 0.5
                }}
              />
            )}
            <IconButton onClick={toggleCollapsed} aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              {isCollapsed
                ? <MenuOutlinedIcon sx={{ color: colors.grey[100] }} />
                : <ChevronLeftIcon sx={{ color: colors.grey[100] }} />}
            </IconButton>
          </Box>
        </SidebarHeader>

        <SidebarContent>
          <Menu iconShape='square'>
            <Item title='Intake' to='/' icon={<PersonAddAltOutlinedIcon />} />
            <Item title='Recents' to='/recents' icon={<ManageSearchOutlinedIcon />} />
            <Item title='Students' to='/students' icon={<SchoolOutlinedIcon />} />
            <Item title='Classes' to='/classes' icon={<ClassOutlinedIcon />} />
            <Item title='Instructors' to='/instructors' icon={<PersonOutlineOutlinedIcon />} />
            <Item title='Reports' to='/reports' icon={<AssessmentOutlinedIcon />} />
            <Item title="Rosters" to="/rosters" icon={<AssignmentOutlinedIcon />} />
            <Item title="Payments" to="/payments" icon={<PaymentsOutlinedIcon />} />
            <Item title='Dashboard' to='/dashboard' icon={<DashboardOutlinedIcon />} />
            {isAdmin() && <Item title='Audit Logs' to='/admin/audit-logs' icon={<ManageSearchOutlinedIcon />} />}
            {isAdmin() && <Item title='AI Keys' to='/admin/ai-keys' icon={<VpnKeyOutlinedIcon />} />}
          </Menu>
        </SidebarContent>

        <SidebarFooter style={{ textAlign: 'center', padding: '10px 0' }}>
          {/* Optional footer */}
        </SidebarFooter>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;